import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Table, Modal, Alert, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { getFaqs, addFaq, updateFaq, deleteFaq } from '../../services/firebase/faqService';

const AdminFAQs = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [current, setCurrent] = useState(null);
  const [validated, setValidated] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getFaqs();
      setFaqs(data);
    } catch (err) {
      setError('Failed to load FAQs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showMessage = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleShowModal = (faq = null) => {
    if (faq) {
      setCurrent({ ...faq });
    } else {
      setCurrent({ question: '', answer: '', answerColor: 'black', active: true });
    }
    setValidated(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrent(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrent((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false || !current.question.trim() || !current.answer.trim()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    try {
      if (current.id) {
        await updateFaq(current.id, current);
        showMessage('FAQ updated successfully.');
      } else {
        await addFaq(current);
        showMessage('FAQ created successfully.');
      }
      handleCloseModal();
      fetchData();
    } catch (err) {
      setError('Failed to save the FAQ. Please try again.');
    }
  };

  const handleMove = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= faqs.length) return;

    // Move within the array, then assign a clean 0..N order to every FAQ.
    const reordered = [...faqs];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    const renumbered = reordered.map((faq, i) => ({ ...faq, order: i }));

    setFaqs(renumbered); // optimistic

    try {
      await Promise.all(renumbered.map((faq) => updateFaq(faq.id, faq)));
    } catch (err) {
      setError('Failed to reorder FAQs.');
      fetchData();
    }
  };

  const confirmDelete = (faq) => {
    setToDelete(faq);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteFaq(toDelete.id);
      showMessage('FAQ deleted successfully.');
      setShowDeleteModal(false);
      setToDelete(null);
      fetchData();
    } catch (err) {
      setError('Failed to delete the FAQ.');
    }
  };

  return (
    <Container className="py-5">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-1">FAQs</h2>
          <p className="text-muted mb-0">
            Manage the questions and answers shown in the FAQ section on your homepage.
          </p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => handleShowModal()}>
            <FontAwesomeIcon icon={faPlus} className="me-2" /> Add FAQ
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : faqs.length === 0 ? (
            <Alert variant="info" className="mb-0">
              No FAQs yet. Click “Add FAQ” to create your first one.
            </Alert>
          ) : (
            <Table responsive hover className="align-middle">
              <thead>
                <tr>
                  <th style={{ width: '90px' }}>Order</th>
                  <th>Question</th>
                  <th>Answer</th>
                  <th>Status</th>
                  <th style={{ width: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {faqs.map((faq, index) => (
                  <tr key={faq.id}>
                    <td>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-1"
                        disabled={index === 0}
                        onClick={() => handleMove(index, -1)}
                        title="Move up"
                      >
                        <FontAwesomeIcon icon={faArrowUp} />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={index === faqs.length - 1}
                        onClick={() => handleMove(index, 1)}
                        title="Move down"
                      >
                        <FontAwesomeIcon icon={faArrowDown} />
                      </Button>
                    </td>
                    <td><strong>{faq.question}</strong></td>
                    <td>
                      <div style={{ maxHeight: '80px', overflow: 'auto', color: 'var(--text-light, #666)' }}>
                        {faq.answer}
                      </div>
                    </td>
                    <td>
                      {faq.active !== false ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="secondary">Hidden</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleShowModal(faq)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => confirmDelete(faq)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{current?.id ? 'Edit FAQ' : 'Add FAQ'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="faqQuestion">
              <Form.Label>Question</Form.Label>
              <Form.Control
                type="text"
                name="question"
                value={current?.question || ''}
                onChange={handleInputChange}
                placeholder="e.g., Do you accept insurance?"
                required
              />
              <Form.Control.Feedback type="invalid">Please provide a question.</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="faqAnswer">
              <Form.Label>Answer</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="answer"
                value={current?.answer || ''}
                onChange={handleInputChange}
                placeholder="Write the answer shown when the question is expanded."
                required
              />
              <Form.Control.Feedback type="invalid">Please provide an answer.</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="faqAnswerColor">
              <Form.Label>Answer text color</Form.Label>
              <Form.Select
                name="answerColor"
                value={current?.answerColor || 'black'}
                onChange={handleInputChange}
              >
                <option value="black">Black</option>
                <option value="white">White</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Sets the color of this answer’s text on the homepage. White is best on a dark background.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-1" controlId="faqActive">
              <Form.Check
                type="checkbox"
                name="active"
                label="Active (show this FAQ on the homepage)"
                checked={current?.active !== false}
                onChange={handleInputChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
            <Button variant="primary" type="submit">Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete confirmation */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this FAQ? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminFAQs;
